import { Button, Checkbox, CircularProgress, createTheme, FormHelperText, InputLabel, MenuItem, Select, TextField, ThemeProvider, Typography, Autocomplete } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import ListItemText from '@mui/material/ListItemText';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { FormattedMessage, useIntl } from 'react-intl';
import ErrorSnackbarNotification from '../../common/ErrorHandling/ErrorSnackbarAlert';
import { styles } from '../../style/style';

const theme = createTheme({
    //sets color of text in dropdown/selectors 
        palette: {
          primary: {
            main: '#00439D',
          },
          secondary: {
            main: '#11cb5f',
          },
        },
      });

export function OrderSummaryAppName(props) {
    return (
        <>
            <Typography id="orderSummaryTitle" variant="h2" sx={styles.h2PageTitle}><><FormattedMessage id='ordersummarycomponents.appname' defaultMessage='Order Summary' /> {props.companyName}</></Typography>
            <Grid container sx={{pt:'2%', pl: '3%'}}>
                <Typography id="orderSummarySearchTitle" variant="h3" sx={styles.h3SectionTitle}><FormattedMessage id='ordersummarycomponents.searchcriteria' defaultMessage='Search Criteria' /></Typography>
            </Grid>
        </>
    )
}

export function ChargeToBacLabel(props) {
    return (
        <>
            <Typography variant="h4" sx={styles.chargeToBacOrPrimaryFanLabelh4InformationGroup}>
                <strong><FormattedMessage id='ordersummarycomponents.chargetobac' defaultMessage='Charge to Business Associate Code (BAC)' /></strong>
            </Typography>
            <Typography variant="caption" sx={styles.chargeToBacOrPrimaryFanLabelPBody}>{props.bacNumber}</Typography>
        </>
    )
}
export function ChargeToBacText(props) {
    const intl = useIntl();
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;
    
    const hasMultipleOptions = (props.options || []).filter(opt => opt !== 'SELECT_ALL').length >= 1;

    if (props.isFleetDealer && hasMultipleOptions) {
        const totalRealOptions = (props.options || []).filter(opt => opt !== 'SELECT_ALL').length;
        const selectedCount = props.selectedValues?.length || 0;
        const allSelected = totalRealOptions > 0 && selectedCount === totalRealOptions;
        const someSelected = selectedCount > 0 && selectedCount < totalRealOptions;
        
        return (
            <>
                <Autocomplete
                    multiple
                    id={props.id}
                    options={props.options || []}
                    disableCloseOnSelect
                    disabled={props.disableSelection === true}
                    value={props.selectedValues || []}
                    onChange={(event, newValue) => {
                        // Create a synthetic event for compatibility with existing handler
                        const syntheticEvent = {
                            target: {
                                value: newValue
                            }
                        };
                        props.onChangeChargeToBacSelection(syntheticEvent);
                    }}
                    getOptionLabel={(option) => option}
                    renderTags={(value, getTagProps) => {
                        const totalOptions = props.options?.filter(opt => opt !== 'SELECT_ALL').length || 0;
                        
                        // If all options are selected, show "All BACs Selected"
                        if (value.length === totalOptions) {
                            return (
                                <span style={{ 
                                    backgroundColor: '#e0e0e0', 
                                    borderRadius: '2px', 
                                    padding: '4px 10px', 
                                    margin: '2px',
                                    fontSize: '0.700rem',
                                    fontWeight: '600'
                                }}>
                                    All BACs Selected ({totalOptions})
                                </span>
                            );
                        }
                        
                        // If more than 3 selected, show first 3 + count of remaining
                        if (value.length > 3) {
                            const firstThree = value.slice(0, 3);
                            const remainingCount = value.length - 3;
                            
                            return (
                                <>
                                    {firstThree.map((option, index) => (
                                        <span key={index} {...getTagProps({ index })} style={{ 
                                            backgroundColor: '#e0e0e0', 
                                            borderRadius: '2px', 
                                            padding: '2px 8px', 
                                            margin: '2px',
                                            fontSize: '0.800rem'
                                        }}>
                                            {option}
                                        </span>
                                    ))}
                                    <span style={{ 
                                        backgroundColor: '#e0e0e0', 
                                        color: '#240606',
                                        borderRadius: '2px', 
                                        padding: '2px 8px', 
                                        margin: '2px',
                                        fontSize: '0.800rem',
                                        fontWeight: '500'
                                    }}>
                                        +{remainingCount} more
                                    </span>
                                </>
                            );
                        }
                        
                        // If 3 or fewer, show individual chips
                        return value.map((option, index) => (
                            <span key={index} {...getTagProps({ index })} style={{ 
                                backgroundColor: '#e0e0e0', 
                                borderRadius: '2px', 
                                padding: '2px 8px', 
                                margin: '2px',
                                fontSize: '0.875rem'
                            }}>
                                {option}
                            </span>
                        ));
                    }}
                    renderOption={(renderProps, option, { selected }) => {
                        // Add "Select All" option at the top
                        if (option === 'SELECT_ALL') {
                            return (
                                <li {...renderProps} style={{ fontWeight: 'bold', borderBottom: '1px solid #ccc' }}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                    />
                                    {allSelected ? 'Clear All' : 'Select All'}
                                </li>
                            );
                        }
                        
                        return (
                            <li {...renderProps}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option}
                            </li>
                        );
                    }}
                    renderInput={(params) => {
                        return (
                            <TextField
                                {...params}
                                variant="filled"
                                label={intl.formatMessage({ id: "ordersummarycomponents.chargetobac", defaultMessage: "Charge to Business Associate Code (BAC)" })}
                                placeholder="Search or select BACs..."
                                error={props.error !== "" && props.error !== null}
                            />
                        );
                    }}
                    sx={styles.orderSummarySelect}
                />
            </>
        )
    }

    return (
        <>
            <TextField
                variant='filled'
                value={props.value}
                id={props.id}
                sx={styles.orderSummaryTextField}
                InputLabelProps={{ style: { fontSize: 16 } }}
                label={intl.formatMessage({ id: "ordersummarycomponents.chargetobac", defaultMessage: "Charge to Business Associate Code (BAC)" })}
                onChange={props.onChangeChargeToBac}
                error={props.error !== "" && props.error !== null} />
        </>
    )
}
export function ShipToBacLabel(props) {
    const shipToLabelId = props.noChildBacs ? 'ordersummarycomponents.shiptobacshort' : 'ordersummarycomponents.shiptobac';
    const shipToLabelDefault = props.noChildBacs ? 'Ship-To BAC' : 'Ship-To Business Associate Code (BAC)';
    return (
        <>
            <Typography variant="h4" sx={styles.chargeToBacOrPrimaryFanLabelh4InformationGroup}>
                <strong><FormattedMessage id={shipToLabelId} defaultMessage={shipToLabelDefault} /></strong>
            </Typography>
            <Typography variant="caption" sx={styles.chargeToBacOrPrimaryFanLabelPBody}>{props.bacNumber}</Typography>
        </>
    )
}
export function PrimaryFanLabel(props) {
    return (
        <>
            <Typography variant="h4" sx={styles.chargeToBacOrPrimaryFanLabelh4InformationGroup}>
                {props.partnerType === 'FLEET' && (<strong><FormattedMessage id='ordersummarycomponents.fan' defaultMessage='Fleet Account Number (FAN)' /></strong>)}
                {props.partnerType !== 'FLEET' && (<strong><FormattedMessage id='ordersummarycomponents.primaryfan' defaultMessage='Primary Fleet Account Number (FAN)' /></strong>)}
            </Typography>
            <Typography variant="caption" sx={styles.chargeToBacOrPrimaryFanLabelPBody}>{props.primaryFan}</Typography>
        </>
    )
}
export function PrimaryFanText(props) {
    const intl = useIntl();
    return (
        <>
            <TextField
                variant='filled'
                value={props.value}
                id={props.id}
                sx={styles.orderSummaryTextField}
                InputLabelProps={{ style: { fontSize: 16 } }}
                label={intl.formatMessage({ id: "ordersummarycomponents.primaryfan", defaultMessage: "Primary Fleet Account Number (FAN)" })}
                onChange={props.onChangePrimaryFan}
                error={props.error !== "" && props.error !== null} />
        </>
    )
}
export function EndUserFan(props) {
    const intl = useIntl();
    return (
        <>
            <TextField
                variant='filled'
                value={props.value}
                id={props.id}
                sx={styles.orderSummaryTextField}
                InputLabelProps={{ style: { fontSize: 16 } }}
                label={intl.formatMessage({ id: "ordersummarycomponents.enduserfan", defaultMessage: "End User Fleet Account Number (FAN)" })}
                onChange={props.onChangeEndUserFan}
                error={props.error !== "" && props.error !== null} />
        </>
    )
}
export function ModelYear(props) {
    return (
        <>
            <FormControl variant='filled' sx={styles.orderSummarySelect}>
            <ThemeProvider theme={theme}>
                <InputLabel><FormattedMessage id="ordersummarycomponents.modelyear" defaultMessage="Model Year" /></InputLabel>
            </ThemeProvider>
                <Select
                    id="modelYear"
                    variant='filled'
                    labelId="modelYear"
                    multiple
                    value={props.selectedYear}
                    onChange={props.handleCheck}
                    renderValue={(selected) => selected.join(', ')}
                    MenuProps={{ disableScrollLock: true }}
                    error={props.modelYearRequired !== ""}
                >
                    {props.options.map((option) => (
                        <MenuItem id={option} key={option} value={option}>
                            <Checkbox checked={props.selectedYear.indexOf(option) > -1} />
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText hidden={props.modelYearRequired === ""} error>{props.modelYearRequired}</FormHelperText>
            </FormControl>
        </>
    )
}

export function ChargeToBfc(props) {
    const intl = useIntl();
    return (
        <>
            <TextField
                variant='filled'
                value={props.value}
                id={props.id}
                sx={styles.orderSummaryTextField}
                InputLabelProps={{ style: { fontSize: 16 } }}
                label={intl.formatMessage({ id: "ordersummarycomponents.chargetobfc", defaultMessage: "Charge-To Function Code (BFC)" })}
                onChange={props.onChangeChargeBfc}
                error={props.error !== "" && props.error !== null} />
        </>
    )
}
export function ShipToBac(props) {
    const intl = useIntl();
    if (props.isUpfitter) {
        return (
            <>
                <FormControl variant='filled' sx={styles.orderSummarySelect}>
                    <ThemeProvider theme={theme}>
                        <InputLabel>{intl.formatMessage({ id: "ordersummarycomponents.shiptobac", defaultMessage: "Ship-To Business Associate Code (BAC)" })}</InputLabel>
                    </ThemeProvider>
                    <Select
                        id={props.id}
                        variant='filled'
                        multiple
                        disabled={props.disableSelection === true}
                        value={props.selectedValues || []}
                        onChange={props.onChangeShipBacSelection}
                        renderValue={(selected) => selected.join(', ')}
                        MenuProps={{ disableScrollLock: true }}
                        error={props.error !== "" && props.error !== null}
                    >
                        {(props.options || []).map((option) => (
                            <MenuItem id={`shipToBac-${option}`} key={option} value={option}>
                                <Checkbox checked={(props.selectedValues || []).indexOf(option) > -1} />
                                <ListItemText primary={option} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </>
        )
    }

    return (
        <>
            <TextField
                variant='filled'
                value={props.value}
                id={props.id}
                sx={styles.orderSummaryTextField}
                InputLabelProps={{ style: { fontSize: 16 } }}
                label={intl.formatMessage({ id: "ordersummarycomponents.shiptobac", defaultMessage: "Ship-To Business Associate Code (BAC)" })}
                onChange={(e) => {
                    const cleaned = e.target.value.replace(/[ ,]/g, '');
                    props.onChangeShipBac({ ...e, target: { ...e.target, value: cleaned } });
                }}
                onKeyDown={(e) => { if (e.key === ' ' || e.key === ',') e.preventDefault(); }}
                error={props.error !== "" && props.error !== null} />
        </>
    )
}


export function ClearButton(props) {
    return (
        <>
            <Button id="clearButton" sx={styles.orderSummaryClear} onClick={props.clearForm} ><FormattedMessage id='ordersummarycomponents.clear' defaultMessage='Clear' /></Button>
        </>
    )
}


export function SearchButton(props) {
   
    const {open,handleClose,errorType} = props
    let errorMessage = []

    if (props.statusMessage !== "" && props.statusMessage !== null) {
        errorMessage.push(props.statusMessage+"\n")
    }
    if (props.invalidChargeToBacMessage !== "" && props.invalidChargeToBacMessage !== null) {
        errorMessage.push(props.invalidChargeToBacMessage+"\n")
    }
    if (props.invalidChargeToBfcMessage !== "" && props.invalidChargeToBfcMessage !== null) {
        errorMessage.push(props.invalidChargeToBfcMessage +"\n")
    }

    if (props.invalidEndUserFanMessage !== "" && props.invalidEndUserFanMessage !== null) {
        errorMessage.push(props.invalidEndUserFanMessage+"\n")
    }
    if (props.invalidPrimaryFanMessage !== "" && props.invalidPrimaryFanMessage !== null) {
        errorMessage.push(props.invalidPrimaryFanMessage+"\n")
    }
    if (props.invalidShipToBacMessage !== "" && props.invalidShipToBacMessage !== null) {
        errorMessage.push(props.invalidShipToBacMessage+"\n")
    }

    return (
        <>
            <Button 
                id="searchButton" 
                sx={styles.orderSummarySearch} 
                onClick={props.submitHandler}
                disabled={props.loading}
            >
                {props.loading ? (
                    <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        <FormattedMessage id='ordersummarycomponents.searching' defaultMessage='Searching...' />
                    </>
                ) : (
                    <FormattedMessage id='ordersummarycomponents.search' defaultMessage='Search' />
                )}
            </Button>
            {errorMessage.length>0  &&
                <ErrorSnackbarNotification errorType={errorType} open={open} handleClose={handleClose} errorMessage={errorMessage} />
            }
        </>
    )
}
